import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import moment from "moment";
import { EventBus, EVENTS } from "src/services/eventEmitter";

import { getAIChatById, getAIChats } from "../../../../queries/ai.graphql";
import {
  upsertAIChatMessage,
  updateAIChatMessageFeedback,
} from "../../../../queries/mutations/aiChatMutations.graphql";

/**
 * @typedef {import('../../../../components/AIContent/types').Message} Message
 * @typedef {import('../../../../components/AIContent/types').CreateChatMessage} CreateChatMessage
 */

const useGetAIChat = () => {
  const [chatId, setChatId] = useState(null);

  const [currentChat, setCurrentChat] = useState(null);

  // Tracks a chatId that was adopted from a streaming response, so we skip
  // the getAIChatById query for it (we already have the data from the stream).
  const adoptedChatIdRef = useRef(null);

  useEffect(() => {
    const onDashboardSelected = (id) => {
      setChatId(id);
    };

    EventBus.on(EVENTS.AI_DASHBOARD_SELECTED, onDashboardSelected);

    return () => {
      EventBus.off(EVENTS.AI_DASHBOARD_SELECTED, onDashboardSelected);
    };
  }, []);

  const {
    data: chatsData,
    loading: chatsLoading,
    error: chatsError,
    refetch: refetchChats,
  } = useQuery(getAIChats, {});

  const chats = useMemo(() => chatsData?.chats || [], [chatsData?.chats]);

  const sortedChats = useMemo(() => {
    if (!Array.isArray(chats) || chats.length === 0) {
      return [];
    }
    // Sort by 'createdAt' or 'updatedAt' in descending order (most recent first)
    return [...chats].sort((a, b) => {
      const dateA = moment(parseInt(a.updatedAt || a.createdAt, 10)).diff(
        moment()
      );
      const dateB = moment(parseInt(b.updatedAt || b.createdAt, 10)).diff(
        moment()
      );

      return dateB - dateA;
    });
  }, [chats]);

  const {
    data: currentChatData,
    loading: chatLoading,
    error: chatError,
    updateQuery,
  } = useQuery(getAIChatById, {
    variables: { id: chatId },
    skip: !chatId || chatId === adoptedChatIdRef.current,
    fetchPolicy: "network-only",
  });

  const [upsertAIChatMessageMutation] = useMutation(upsertAIChatMessage);
  const [updateAIChatMessageFeedbackMutation] = useMutation(
    updateAIChatMessageFeedback
  );

  const updateChatOptimistically = useCallback((message) => {
    setCurrentChat((prevChat) => {
      const prevMessages = prevChat?.messages || [];
      const hasMessage = prevMessages?.some((m) => m.id === message.id);

      const updatedMessages = hasMessage
        ? prevMessages.map((m) => (m.id === message.id ? message : m))
        : [...prevMessages, message];

      return {
        ...prevChat,
        messages: updatedMessages,
      };
    });
  }, []);

  /**
   * Submits feedback for a specific message
   * @param {Object} feedback - The feedback object
   * @param {string} feedback.messageId - The ID of the message
   * @param {string} feedback.feedbackRating - 'positive' or 'negative'
   * @param {string} [feedback.feedbackComment] - Optional comment
   */
  const onSubmitMessageFeedback = useCallback(
    async ({ messageId, feedbackRating, feedbackComment }) => {
      if (!chatId || !messageId) {
        return null;
      }

      const result = await updateAIChatMessageFeedbackMutation({
        variables: {
          input: {
            id: messageId,
            aiChatId: chatId,
            feedbackRating,
            feedbackComment,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          message: {
            __typename: "AIChatMessage",
            id: messageId,
            feedbackRating,
            feedbackComment,
          },
        },
        update: (_, response) => {
          const updatedMessage = response.data.message;

          // Update the message in local state with feedback
          setCurrentChat((prevChat) => {
            if (!prevChat) {
              return prevChat;
            }

            const updatedMessages = prevChat.messages.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    feedbackRating: updatedMessage.feedbackRating,
                    feedbackComment: updatedMessage.feedbackComment,
                  }
                : m
            );

            return {
              ...prevChat,
              messages: updatedMessages,
            };
          });
        },
      });

      return result;
    },
    [chatId, updateAIChatMessageFeedbackMutation]
  );

  /**
   * @type {CreateChatMessage}
   */
  const onUpsertAIChatMessage = useCallback(
    async (message) => {
      if (!chatId) {
        return null;
      }

      const result = await upsertAIChatMessageMutation({
        variables: {
          input: {
            ...message,
            aiChatId: chatId,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          message: {
            __typename: "AIChatMessage",
            id: message.id,
            aiChatId: chatId,
            role: message.role,
            content: message.content,
            status: message.status,
            citations: message.citations || [],
            respondsTo: message.respondsTo || null,
            tools: message.tools || [],
            createdAt: message.createdAt ?? null,
            updatedAt: message.updatedAt ?? null,
          },
        },
        update: (proxy, response) => {
          const newMessage = response.data.message;

          updateChatOptimistically(newMessage);
          // Update title of chat if user message is added for updating the title in ChatHistoryPanel.
          if (message.role === "user") {
            updateQuery((prevQuery) => {
              return {
                chat: {
                  ...prevQuery.chat,
                  title: message.content,
                },
              };
            });
          }
        },
      });

      return result;
    },
    [chatId, upsertAIChatMessageMutation, updateChatOptimistically, updateQuery]
  );

  // Tracks whether the user intentionally started a new chat,
  // so the auto-select effect doesn't immediately re-select an old one.
  const isNewChat = useRef(false);

  const onStartNewChat = useCallback(() => {
    adoptedChatIdRef.current = null;
    isNewChat.current = true;
    setChatId(null);
    setCurrentChat(null);
  }, [setChatId, setCurrentChat]);

  const onSelectChat = useCallback(
    (selectedChatId) => {
      adoptedChatIdRef.current = null;
      isNewChat.current = false;
      setChatId(selectedChatId);
    },
    [setChatId]
  );

  // Adopt a chatId returned by a streaming response without re-fetching it,
  // since we already have the messages from the stream.
  const adoptChatId = useCallback(
    (newChatId) => {
      adoptedChatIdRef.current = newChatId;
      isNewChat.current = false;
      setChatId(newChatId);
      setCurrentChat((prev) =>
        prev ? { ...prev, id: newChatId } : { id: newChatId, messages: [] }
      );
    },
    [setChatId]
  );

  useEffect(() => {
    if (currentChatData?.chat && currentChatData.chat.id !== currentChat?.id) {
      setCurrentChat(currentChatData.chat);
    }
  }, [currentChatData, currentChat]);

  // Auto-select the most recent chat on initial load
  useEffect(() => {
    if (sortedChats && sortedChats.length && !chatId && !isNewChat.current) {
      const defaultChatId = sortedChats?.[0]?.id;
      setChatId(defaultChatId);
    }
  }, [sortedChats, chatId]);

  return {
    chatId,
    chats: sortedChats,
    currentChat,
    loading: chatsLoading || chatLoading,
    error: chatsError || chatError,
    onUpsertAIChatMessage,
    updateChatOptimistically,
    onStartNewChat,
    onSelectChat,
    adoptChatId,
    onSubmitMessageFeedback,
    refetchChats,
  };
};

export default useGetAIChat;
